import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaBuilding, FaFolderOpen, FaGlobe, FaEnvelope, FaPhone, FaPlus, FaLayerGroup,
    FaLock, FaPen, FaShieldAlt, FaStar, FaRegStar, FaArchive, FaUndo, FaSlidersH, FaChartBar,
} from "react-icons/fa";
import { COMPANIES_CONSTANTS } from "../_MODULES/_API";
import { COMPANY_TABS } from "../_MODULES/_Navigation";
import { PROJECTS_CONSTANTS } from "../../_Projects/_MODULES/_API";
import { STATUS_MAP_PROJECT } from "../../../_Catalogs.Views";
import { ProjectFormModal } from "../../_Projects/_UI/ProjectFormModal";
import { CompanyFormModal } from "../_UI/CompanyFormModal";
import { PanelVault } from "../_UI/PanelVault";
import { PageHeader, PageHeaderMeta } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { StatusBadge } from "../../../../../_CORE/APP/Templates/_UI/Status/_index";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { Card, EmptyState, Spinner, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import "../_UI/_Styles/Companies.css";

const PROJECT_SCOPE_OPTIONS = [
    { value: "ACTIVE", label: "Proyectos activos" },
    { value: "ARCHIVED", label: "Proyectos archivados" },
];

export const CompanyDetailViewMain = () => {
    const { id, tab } = useParams();
    const ACTIVE_TAB = ["projects", "vault"].includes(tab) ? tab : "projects";
    const navigate = useNavigate();

    const [useSearch, setSearch] = useState("");
    const [useProjectScope, setProjectScope] = useState("ACTIVE");
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [useIsModalOpen, setIsModalOpen] = useState(false);
    const [useIsEditOpen, setIsEditOpen] = useState(false);
    const [useProjectEdit, setProjectEdit] = useState(null);
    const [useLocalProjects, setLocalProjects] = useState([]);
    const [useArchivedProjects, setArchivedProjects] = useState([]);
    const [useDescriptionExpanded, setDescriptionExpanded] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", status: "ALL", stack: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const { apiGet: onGetCompany, useApiData: COMPANY, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 300, enablePagination: false, startLoading: true,
    });
    const { apiGet: onGetArchivedProjects, useApiData: ARCHIVED_PROJECTS } = useApiQuery({
        minTimeQuery: 200, enablePagination: false,
    });

    const onLoadCompany = () => onGetCompany({
        endpoint: COMPANIES_CONSTANTS.DETAIL, isDetail: true, config: { pathParams: { id } },
    });

    useEffect(() => {
        onLoadCompany();
        onGetArchivedProjects({ endpoint: PROJECTS_CONSTANTS.ARCHIVE_LIST });
        setDescriptionExpanded(false);
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setLocalProjects((COMPANY?.projects ?? []).filter((project) => !project.archivedAt));
    }, [COMPANY]);

    useEffect(() => {
        // La pestaña nueva arranca con su estado visual cerrado, sin heredar el filtro expandido.
        setAdvancedOpen(false);
        setSearch("");
    }, [ACTIVE_TAB]);

    useEffect(() => {
        setArchivedProjects((ARCHIVED_PROJECTS ?? []).filter((project) => (
            project.companyId === id || project.company?.id === id
        )));
    }, [ARCHIVED_PROJECTS, id]);

    const SOURCE_PROJECTS = useProjectScope === "ARCHIVED" ? useArchivedProjects : useLocalProjects;
    const PROJECTS = [...SOURCE_PROJECTS].sort((a, b) =>
        (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || String(a.name ?? "").localeCompare(String(b.name ?? ""))
    );

    const VISIBLE = PROJECTS
        .filter((project) => advanced.favorite === "ALL" || (!!project.isFavorite === (advanced.favorite === "YES")))
        .filter((project) => advanced.status === "ALL" || project.status === advanced.status)
        .filter((project) => !String(advanced.stack || "").trim() || onMatchesSearch(advanced.stack, project.stack || []))
        .filter((project) => onDateMatches(advanced.dateField === "CREATED" ? project.createdAt : project.updatedAt || project.createdAt, advanced.dateRange, advanced.dateExact))
        .filter((project) => onMatchesSearch(useSearch, [
            project.name, project.status, project.description, project.repositoryUrl,
            ...(project.stack ?? []),
            ...(project.subProjects ?? []).flatMap((subProject) => [
                subProject.name, subProject.type, subProject.description, ...(subProject.stack ?? []),
            ]),
        ]));

    const advancedCount = [advanced.favorite !== "ALL", advanced.status !== "ALL", String(advanced.stack || "").trim(), advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ favorite: "ALL", status: "ALL", stack: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const PAGER = usePagedList(VISIBLE, 6, `companyProjects-${useProjectScope}`);

    if (IS_LOADING) return <Spinner label="Cargando compañía…" />;
    if (!COMPANY) return <EmptyState icon={<FaBuilding />} title="No encontramos esa compañía" />;

     const COMPANY_DESCRIPTION = String(COMPANY.description ?? "").trim();
    const IS_LONG_DESCRIPTION = COMPANY_DESCRIPTION.length > 260;

    const onOpenCreate = () => {
        setProjectEdit(null);
        setIsModalOpen(true);
    };

    const onOpenProjectEdit = (project) => {
        setProjectEdit(project);
        setIsModalOpen(true);
    };

    const onPatchProjectLocal = (projectId, patch) => {
        setLocalProjects((rows) => rows.map((row) => row.id === projectId ? { ...row, ...patch } : row));
        setArchivedProjects((rows) => rows.map((row) => row.id === projectId ? { ...row, ...patch } : row));
    };

    const onToggleFavorite = async (project) => {
        const previous = !!project.isFavorite;
        const next = !previous;
        onPatchProjectLocal(project.id, { isFavorite: next });
        const response = await onSilentPatch({
            endpoint: PROJECTS_CONSTANTS.UPDATE, id: project.id, data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del proyecto.",
        });
        if (!response.success) onPatchProjectLocal(project.id, { isFavorite: previous });
    };

    const onArchiveProject = async (project) => {
        const ok = await confirm.danger(
            "Archivar proyecto",
            `Se archivará “${project.name}”. Sus subproyectos, credenciales y bitácora se conservan.`,
        );
        if (!ok) return;

        setLocalProjects((rows) => rows.filter((row) => row.id !== project.id));
        setArchivedProjects((rows) => [{ ...project, archivedAt: new Date().toISOString() }, ...rows]);
        const response = await onApiAction({
            method: "PATCH", endpoint: PROJECTS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: project.id }, successMessage: "Proyecto archivado",
        });
        if (!response.success) {
            setArchivedProjects((rows) => rows.filter((row) => row.id !== project.id));
            setLocalProjects((rows) => [project, ...rows]);
        }
    };

    const onRestoreProject = async (project) => {
        setArchivedProjects((rows) => rows.filter((row) => row.id !== project.id));
        setLocalProjects((rows) => [{ ...project, archivedAt: null }, ...rows]);
        const response = await onApiAction({
            method: "PATCH", endpoint: PROJECTS_CONSTANTS.RESTORE_ONE,
            pathParams: { id: project.id }, successMessage: "Proyecto restaurado",
        });
        if (!response.success) {
            setLocalProjects((rows) => rows.filter((row) => row.id !== project.id));
            setArchivedProjects((rows) => [project, ...rows]);
        }
    };

    return (
        <ViewLayout
            header={<div className="LStudio__app_company_detail_header"><PageHeader
                title={COMPANY.name}
                description={COMPANY_DESCRIPTION ? (
                    <span className="LStudio__app_company_description" data-long={IS_LONG_DESCRIPTION} data-expanded={useDescriptionExpanded}>
                        <span className="LStudio__app_company_description_text">{COMPANY_DESCRIPTION}</span>
                        {IS_LONG_DESCRIPTION && (
                            <button type="button" className="LStudio__app_company_description_toggle"
                                aria-expanded={useDescriptionExpanded}
                                onClick={() => setDescriptionExpanded((current) => !current)}>
                                {useDescriptionExpanded ? "Ver menos" : "Ver más"}
                            </button>
                        )}
                    </span>
                ) : null}
                icon={<FaBuilding />}
                backTo="/companies"
                breadcrumbs={[
                    { label: "Compañías", href: "/companies", onClick: (event) => { event.preventDefault(); navigate("/companies"); } },
                    { label: COMPANY.name },
                ]}
                meta={<PageHeaderMeta items={[
                    COMPANY.website && { icon: <FaGlobe />, value: COMPANY.website },
                    COMPANY.contactEmail && { icon: <FaEnvelope />, value: COMPANY.contactEmail },
                    COMPANY.contactPhone && { icon: <FaPhone />, value: COMPANY.contactPhone },
                ]} />}
                actions={<>
                    {ACTIVE_TAB === "projects" && <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />}
                    <AppButton variant="outline" tone="neutral" icon={<FaPen />} onClick={() => setIsEditOpen(true)}>Editar compañía</AppButton>
                    {ACTIVE_TAB === "projects" && useProjectScope === "ACTIVE" && <AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo proyecto</AppButton>}
                </>}
                tabs={COMPANY_TABS.map((item) => ({
                    ...item,
                    icon: item.value === "vault" ? <FaShieldAlt /> : <FaFolderOpen />,
                    to: `/companies/${id}/${item.value}`,
                }))}
                activeTab={ACTIVE_TAB}
                onChangeTab={(value) => { setAdvancedOpen(false); setSearch(""); navigate(`/companies/${id}/${value}`); }} /></div> }>

            {ACTIVE_TAB === "vault" ? <PanelVault companyId={id} /> : <>
                {COMPANY.notes && <p className="LStudio__app_company_detail_notes">{COMPANY.notes}</p>}

                <div className="LStudio__app_panel_bar">
                    <ViewToolbar
                        filters={<>
                            <SearchFilter value={useSearch} onChange={setSearch}
                                total={PROJECTS.length} matches={VISIBLE.length}
                                placeholder="Ej.: Sistema nuevo CRM, React o activo..." />
                            <FieldSelect id="company_project_scope" label={null} value={useProjectScope}
                                onChange={(event) => setProjectScope(event.target.value)} options={PROJECT_SCOPE_OPTIONS} />
                            <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}</AppButton>
                        </>}
                        expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                            <FieldSelect id="company_projects_adv_favorite" label="Destacado" value={advanced.favorite} onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                            <FieldSelect id="company_projects_adv_status" label="Estado" value={advanced.status} onChange={(event) => setAdvanced((current) => ({ ...current, status: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, ...Object.keys(STATUS_MAP_PROJECT || {}).map((value) => ({ value, label: STATUS_MAP_PROJECT[value]?.label || value }))]} />
                            <FieldInput id="company_projects_adv_stack" label="Stack" value={advanced.stack} onChange={(event) => setAdvanced((current) => ({ ...current, stack: event.target.value }))} placeholder="React, PHP, Nest..." />
                            <FieldSelect id="company_projects_adv_date" label="Fecha" value={advanced.dateField} onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))} options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Creación" }]} />
                            <FieldDate id="company_projects_adv_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                            <FieldDate id="company_projects_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                            <FieldDate id="company_projects_adv_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                        </AdvancedFilterPanel>}
                        total={PAGER.total} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                        exportColumns={[
                            { key: "name", label: "Proyecto" },
                            { key: "status", label: "Estado" },
                            { key: "description", label: "Descripción" },
                            { key: "subProjects", label: "Subproyectos" },
                        ]}
                        exportRows={VISIBLE.map((project) => ({
                            name: project.name,
                            status: project.status,
                            description: project.description ?? "",
                            subProjects: project.subProjects?.length ?? 0,
                        }))}
                        exportName={useProjectScope === "ARCHIVED" ? "proyectos-compania-archivados" : "proyectos-compania"} />
                </div>

                {PROJECTS.length === 0 ? (
                    <EmptyState icon={useProjectScope === "ARCHIVED" ? <FaArchive /> : <FaFolderOpen />}
                        title={useProjectScope === "ARCHIVED" ? "Esta compañía no tiene proyectos archivados" : "Esta compañía todavía no tiene proyectos"}
                        description={useProjectScope === "ARCHIVED"
                            ? "Cuando archives un proyecto va a aparecer acá para restaurarlo o editarlo. La eliminación definitiva se hace desde Archivados."
                            : "Creá el primero y después cargale los subproyectos de frontend y backend."}
                        action={useProjectScope === "ACTIVE"
                            ? <AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo proyecto</AppButton>
                            : null} />
                ) : VISIBLE.length === 0 ? (
                    <EmptyState icon={<FaFolderOpen />} title="No encontramos proyectos"
                        description="Probá con otro nombre, estado, subproyecto o tecnología." />
                ) : (
                    <div className="LStudio__app_company_detail_grid">
                        {PAGER.paged.map((project) => (
                            <Card key={project.id} className="LStudio__app_company_detail_project" data-favorite={!!project.isFavorite}>
                                <div className="LStudio__app_company_detail_project_head">
                                    <span className="LStudio__app_company_detail_project_name">
                                        {project.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacado" />}
                                        {project.name}
                                    </span>
                                    <StatusBadge status={project.status} map={STATUS_MAP_PROJECT} size="sm" />
                                    {useProjectScope === "ARCHIVED" && <Badge tone="neutral" size="sm">Archivado</Badge>}
                                    <CardActionMenu title="Acciones del proyecto" items={useProjectScope === "ARCHIVED" ? [
                                        { key: "favorite", icon: project.isFavorite ? <FaStar /> : <FaRegStar />, label: project.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(project) },
                                        { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenProjectEdit(project) },
                                        { key: "restore", icon: <FaUndo />, label: "Restaurar", onClick: () => onRestoreProject(project) },
                                        { divider: true },
                                    ] : [
                                        { key: "favorite", icon: project.isFavorite ? <FaStar /> : <FaRegStar />, label: project.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(project) },
                                        { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenProjectEdit(project) },
                                        { divider: true },
                                        { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchiveProject(project) },
                                    ]} />
                                </div>

                                <span className="LStudio__app_company_detail_project_desc">
                                    {project.description || "Sin descripción"}
                                </span>

                                <span className="LStudio__app_company_detail_project_meta">
                                    <span><FaLayerGroup /> {project.subProjects?.length ?? 0} subproyectos</span>
                                    {project.hasAccessPin && <span><FaLock /> con pin</span>}
                                </span>

                                {useProjectScope === "ACTIVE" && <div className="LStudio__app_company_detail_project_actions">
                                    <AppButton size="sm" variant="soft" to={`/projects/${project.id}/subprojects`}>Abrir</AppButton>
                                </div>}
                            </Card>
                        ))}
                    </div>
                )}
            </>}

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            {ACTIVE_TAB === "projects" && <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title={`Estadísticas · ${COMPANY.name}`} description="Resumen de los proyectos de esta compañía."
                metrics={[
                    { key: "projects", label: "Proyectos", value: useLocalProjects.length, icon: <FaFolderOpen />, tone: "accent", footer: `${useLocalProjects.filter((project) => project.status === "ACTIVE").length} activos · ${useLocalProjects.filter((project) => project.status === "PAUSED").length} pausados` },
                    { key: "subprojects", label: "Subproyectos", value: useLocalProjects.reduce((total, project) => total + (project.subProjects?.length ?? 0), 0), icon: <FaLayerGroup />, tone: "neutral" },
                    { key: "finished", label: "Terminados", value: useLocalProjects.filter((project) => project.status === "FINISHED").length, icon: <FaFolderOpen />, tone: "ok", footer: `${useArchivedProjects.length} archivados` },
                    { key: "pin", label: "Con PIN", value: useLocalProjects.filter((project) => project.hasAccessPin).length, icon: <FaLock />, tone: "warn", footer: "Proyectos con credenciales protegidas" },
                ]} />}

            <ProjectFormModal
                isOpen={useIsModalOpen}
                project={useProjectEdit}
                projects={[...useLocalProjects, ...useArchivedProjects]}
                companies={[COMPANY]}
                defaultCompanyId={COMPANY.id}
                isCompanyLocked
                onClose={() => setIsModalOpen(false)}
                onSaved={(saved, isEdit) => {
                    if (isEdit) {
                        onPatchProjectLocal(saved.id, saved);
                        return;
                    }
                    setLocalProjects((prev) => [saved, ...prev]);
                }} />

            <CompanyFormModal
                isOpen={useIsEditOpen}
                company={COMPANY}
                onClose={() => setIsEditOpen(false)}
                onSaved={(saved) => mutateLocal({ id: COMPANY.id, patch: saved })} />

        </ViewLayout>
    );
};
